import type { ShellIntent } from './types';

const NAV_ALIASES: Record<string, { path: string; label: string }> = {
  home: { path: '/', label: 'Home' },
  dashboard: { path: '/dashboard', label: 'Dashboard' },
  profile: { path: '/profile', label: 'Profile' },
  contacts: { path: '/contacts', label: 'Contacts' },
  inbox: { path: '/inbox', label: 'Inbox' },
  messages: { path: '/messages', label: 'Messages' },
  conversations: { path: '/conversations', label: 'Conversations' },
  transactions: { path: '/transactions', label: 'Transactions' },
  'payment sources': { path: '/payment-sources', label: 'Payment Sources' },
  'payment methods': { path: '/payment-sources', label: 'Payment Sources' },
  topup: { path: '/topup', label: 'Top Up' },
  'top up': { path: '/topup', label: 'Top Up' },
  transfer: { path: '/transfer', label: 'Transfer' },
  marketplace: { path: '/marketplace', label: 'Marketplace' },
  merchants: { path: '/marketplace?tab=stores', label: 'Merchants' },
  portfolio: { path: '/portfolio', label: 'Portfolio' },
  redeem: { path: '/redeem', label: 'Redeem' },
  memoney: { path: '/memoney', label: 'MeMoney' },
  'referral tokens': { path: '/referral-tokens', label: 'Referral Tokens' },
  referrals: { path: '/referral-tokens', label: 'Referral Tokens' },
  help: { path: '/help', label: 'Help' },
  support: { path: '/support', label: 'Support' },
  pos: { path: '/pos', label: 'Point of Sale' },
  exchange: { path: '/exchange', label: 'Exchange' },
  'currency market': { path: '/currency-market', label: 'Currency Market' },
  'merchant tools': { path: '/merchant-tools', label: 'Merchant Tools' },
  'merchant onboarding': { path: '/merchant-onboarding', label: 'Merchant Onboarding' },
};

export function getCurrencyFromSymbol(text: string): string | null {
  if (text.includes('$')) return 'USD';
  if (text.includes('€')) return 'EUR';
  if (text.includes('£')) return 'GBP';
  return null;
}

export function parseAmountCurrency(
  command: string,
  defaultCurrency: string
): { amount: number; currency: string } | null {
  const patterns = [
    /(?:\$|€|£)\s*(\d+(?:\.\d+)?)/,
    /(\d+(?:\.\d+)?)\s*(USD|EUR|GBP|CAD|AUD|CHF|JPY)\b/i,
    /(\d+(?:\.\d+)?)\s*(?:dollars?|euros?|pounds?)/i,
    /\b(\d+(?:\.\d+)?)\b/,
  ];

  for (const pattern of patterns) {
    const match = command.match(pattern);
    if (match) {
      const amount = parseFloat(match[1]);
      if (!Number.isFinite(amount) || amount <= 0) continue;
      const explicitCurrency = match[2]?.toUpperCase();
      const currency =
        explicitCurrency ?? getCurrencyFromSymbol(command) ?? defaultCurrency;
      return { amount, currency };
    }
  }
  return null;
}

function parseNavigateIntent(cmd: string): ShellIntent | null {
  const openMatch = cmd.match(
    /^(?:go to|open|show|navigate to|visit)\s+(.+)$/i
  );
  const target = openMatch?.[1]?.trim().toLowerCase();
  if (!target) return null;

  for (const [alias, route] of Object.entries(NAV_ALIASES)) {
    if (target === alias || target.startsWith(`${alias} `)) {
      return { type: 'navigate', path: route.path, label: route.label };
    }
  }
  return null;
}

/** Map natural language to a structured intent (no network I/O). */
export function parseCommand(command: string, defaultCurrency = 'USD'): ShellIntent {
  const raw = command.trim();
  const cmd = raw.toLowerCase();

  if (!raw) {
    return { type: 'unknown', raw };
  }

  if (
    cmd === 'help' ||
    cmd.includes('what can you do') ||
    cmd.includes('commands') ||
    cmd === '?'
  ) {
    return { type: 'help' };
  }

  const nav = parseNavigateIntent(cmd);
  if (nav) return nav;

  if (
    cmd.includes('my profile') ||
    cmd === 'profile' ||
    cmd.includes('who am i') ||
    cmd.includes('account info')
  ) {
    return { type: 'profile' };
  }

  if (
    cmd.includes('payment method') ||
    cmd.includes('payment source') ||
    cmd.includes('funding source') ||
    cmd.includes('my cards') ||
    cmd.includes('my banks')
  ) {
    return { type: 'payment-methods' };
  }

  if (
    cmd.includes('subscription') ||
    cmd.includes('subscribed merchant')
  ) {
    return { type: 'subscriptions' };
  }

  if (cmd.includes('support ticket')) {
    return { type: 'support-tickets' };
  }

  if (
    cmd.includes('referral') &&
    !cmd.includes('create') &&
    !cmd.includes('token')
  ) {
    return { type: 'referrals' };
  }

  const inboxMatch =
    cmd.includes('inbox') ||
    cmd.includes('notifications') ||
    (cmd.includes('messages') && cmd.includes('merchant'));
  if (inboxMatch) {
    return { type: 'inbox', unreadOnly: cmd.includes('unread') };
  }

  const contactSearch = raw.match(
    /(?:search contacts?|find contact|look up contact)\s+(.+)/i
  );
  if (contactSearch) {
    return { type: 'contacts', search: contactSearch[1].trim() };
  }
  if (
    cmd.includes('contacts') ||
    cmd.includes('counterparties') ||
    cmd.includes('my contacts')
  ) {
    return { type: 'contacts' };
  }

  const merchantSearch = raw.match(
    /(?:search merchants?|find (?:store|merchant|shop)|merchant search)\s+(.+)/i
  );
  if (merchantSearch) {
    return { type: 'search-merchants', query: merchantSearch[1].trim() };
  }

  if (cmd.includes('marketplace') && cmd.includes('search')) {
    const q = raw.match(/search\s+(.+)/i)?.[1]?.trim();
    return { type: 'search-marketplace', query: q };
  }

  const transferOutMatch = raw.match(
    /transfer\s+out(?:\s+(USD|EUR|GBP|\w{3}))?/i
  );
  if (transferOutMatch || cmd.includes('withdraw') || cmd.includes('payout')) {
    return {
      type: 'transfer-out',
      currency: transferOutMatch?.[1]?.toUpperCase(),
    };
  }

  const topupMatch = parseAmountCurrency(raw, defaultCurrency);
  if (
    cmd.includes('topup') ||
    cmd.includes('top up') ||
    cmd.includes('add funds') ||
    cmd.includes('add money') ||
    cmd.includes('fund account') ||
    cmd.includes('deposit')
  ) {
    return {
      type: 'topup',
      amount: topupMatch?.amount,
      currency: topupMatch?.currency ?? defaultCurrency,
    };
  }

  const transferMatch = raw.match(
    /(?:transfer|send)\s+(?:\$|€|£)?(\d+(?:\.\d+)?)\s*(?:(\w{3})\s+)?(?:to\s+(.+))?/i
  );
  if (transferMatch && (transferMatch[3] || cmd.startsWith('transfer') || cmd.startsWith('send'))) {
    return {
      type: 'transfer',
      amount: parseFloat(transferMatch[1]),
      currency:
        transferMatch[2]?.toUpperCase() ??
        getCurrencyFromSymbol(raw) ??
        defaultCurrency,
      recipient: transferMatch[3]?.trim(),
    };
  }

  const txDaysMatch = cmd.match(/transactions?\s+last\s+(\d+)\s+days?/);
  if (cmd.includes('transaction') || cmd.includes('history') || cmd.includes('activity')) {
    return {
      type: 'transactions',
      days: txDaysMatch ? parseInt(txDaysMatch[1], 10) : undefined,
      limit: 20,
    };
  }

  const balanceCurrencyMatch = cmd.match(
    /balance\s+(USD|EUR|GBP|CAD|AUD|CHF|JPY|\w{3})\b/i
  );
  if (
    cmd.includes('balance') ||
    cmd.includes('balances') ||
    (cmd.includes('money') && (cmd.includes('how much') || cmd.includes('what')))
  ) {
    return {
      type: 'balances',
      currency: balanceCurrencyMatch?.[1]?.toUpperCase(),
    };
  }

  const tokenInfoMatch = raw.match(
    /(?:token\s+(?:info|details?|lookup)|lookup\s+token|check\s+token)\s+([A-Z0-9]{4,12})/i
  );
  if (tokenInfoMatch) {
    return { type: 'token-info', tokenCode: tokenInfoMatch[1].toUpperCase() };
  }

  const redeemPatterns = [
    /redeem\s+([A-Z0-9]{4,12})/i,
    /use\s+(?:token\s+)?([A-Z0-9]{4,12})/i,
    /claim\s+([A-Z0-9]{4,12})/i,
  ];
  for (const pattern of redeemPatterns) {
    const match = raw.match(pattern);
    if (match) {
      const partial = raw.match(/redeem\s+[A-Z0-9]+\s+(?:for\s+)?(?:\$|€|£)?(\d+(?:\.\d+)?)/i);
      return {
        type: 'redeem',
        tokenCode: match[1].toUpperCase(),
        amount: partial ? parseFloat(partial[1]) : undefined,
      };
    }
  }

  const isInvoice =
    cmd.includes('invoice') ||
    cmd.includes('bill for') ||
    cmd.includes('request payment');

  const createPatterns = [
    /create\s+(?:an?\s+)?(?:invoice|bill)\s+(?:for\s+)?/i,
    /create\s+(?:an?\s+)?(?:\$|€|£)?(\d+(?:\.\d+)?)\s*(?:(\w{3})\s+)?(?:token|payment|qr)/i,
    /make\s+(?:an?\s+)?(?:\$|€|£)?(\d+(?:\.\d+)?)\s*(?:(\w{3})\s+)?(?:token|payment|qr)/i,
    /create\s+token\s+for\s+(?:\$|€|£)?(\d+(?:\.\d+)?)/i,
    /(?:\$|€|£)?(\d+(?:\.\d+)?)\s*(?:(\w{3})\s+)?(?:token|payment|qr)/i,
  ];

  if (
    isInvoice ||
    createPatterns.some((p) => p.test(raw)) ||
    (cmd.includes('create') && (cmd.includes('token') || cmd.includes('payment')))
  ) {
    const parsed = parseAmountCurrency(raw, defaultCurrency);
    if (parsed) {
      const fundAsNeeded =
        cmd.includes('from my') ||
        cmd.includes('from visa') ||
        cmd.includes('from card') ||
        cmd.includes('from bank') ||
        cmd.includes('fund as needed') ||
        cmd.includes('auto fund');
      const messageMatch = raw.match(/(?:message|note|memo)[:\s]+["']?([^"']+)["']?/i);
      return {
        type: 'create-token',
        amount: parsed.amount,
        currency: parsed.currency,
        fundAsNeeded,
        isInvoice,
        message: messageMatch?.[1]?.trim(),
      };
    }
  }

  const bareToken = raw.match(/^([A-Z0-9]{6})$/);
  if (bareToken) {
    return { type: 'redeem', tokenCode: bareToken[1] };
  }

  return { type: 'unknown', raw };
}

export const COMMAND_TEMPLATES = [
  'help',
  'show balances',
  'balance USD',
  'create $10 token',
  'create $50 token',
  'create 25 EUR token',
  'create invoice for $100',
  'redeem ABC123',
  'transactions',
  'transactions last 7 days',
  'show my profile',
  'list contacts',
  'search contacts john',
  'payment methods',
  'open inbox',
  'open topup',
  'open marketplace',
  'search merchants coffee',
  'referrals',
  'go to memoney',
  'transfer $50 to @username',
  'topup $100',
  'transfer out USD',
];
